import { Initiative } from '../modules/admin-growth/types';
import { parseDateSafely } from '../utils/date';

export type GoalStatus = 'not_started' | 'on_track' | 'at_risk' | 'off_track' | 'completed';

// Raw API activity interface
export interface ActivityApiData {
	added_at: string | null;
	added_by_user_id: string | null;
	id: string;
	type: string;
	user_id: string;
	title?: string | null;
	summary?: string;
	source?: string | null; // e.g. "system", "user"
	created_at: string;
	updated_at: string;
	data: any | null; // Keep this in original format for Feedback/Bonus cards
}

// Transformed activity interface (camelCase)
export interface Activity {
	addedAt: string | null;
	addedByUserId: string | null;
	id: string;
	type: string; // e.g., "Growth::GoalUpdate", "bonus", "agenda_topic", "feedback", etc.
	userId: string;
	title?: string | null;
	summary?: string;
	source?: string | null; // e.g. "system", "user"
	createdAt: string;
	updatedAt: string;
	data: any | null; // Keep this in original format for Feedback/Bonus cards
}

export interface UserCollaborator {
	id: string;
	username: string;
	displayName: string;
	displayFirstName?: string;
	firstName?: string;
	lastName?: string;
	email?: string;
	fullPicUrl?: string;
	profilePicUrl?: string;
	createdAt?: string;
	updatedAt?: string;
	preferredFirstName?: string;
}

export interface GoalUpdate {
	id: string;
	goalId: string;
	userId: string;
	title?: string;
	summary: string;
	source?: string;
	createdAt: string;
	updatedAt: string;
}

// Raw API response interface (snake_case)
export interface GoalApiData {
	id: string;
	company_id: string;
	created_by: string;
	user_id: string;
	title: string;
	summary?: string;
	status: GoalStatus;
	tags: string[];
	deleted_at?: string;
	completed_at?: string;
	start_date?: string;
	end_date?: string;
	activities: ActivityApiData[]; // Raw API activities in snake_case
	collaborators: Array<{
		id: string;
		username: string;
		display_name: string;
		display_first_name?: string;
		first_name?: string;
		last_name?: string;
		email?: string;
		full_pic_url?: string;
		profile_pic_url?: string;
		created_at?: string;
		updated_at?: string;
		preferred_first_name?: string;
	}>;
	initiative_id?: string;
	initiative?: any; // Initiative structure may vary
	created_at: string;
	updated_at: string;
	// Abilities added by backend under `abilities`
	abilities?: {
		canView?: boolean;
		canModify?: boolean;
		canDelete?: boolean;
		canRemoveSelf?: boolean;
	};
}

export class Goal {
	id: string;
	companyId: string;
	createdBy: string;
	userId: string;
	title: string;
	summary?: string;
	status: GoalStatus;
	tags: string[];
	deletedAt?: Date;
	completedAt?: Date;
	startDate?: Date;
	endDate?: Date;
	activities: Activity[];
	collaborators: UserCollaborator[];
	initiative_id?: string;
	initiative?: Initiative;
	createdAt: Date;
	updatedAt: Date;
	// Abilities
	abilities: {
		canView: boolean;
		canModify: boolean;
		canDelete: boolean;
		canRemoveSelf: boolean;
	};

	constructor(data: GoalApiData) {
		this.id = data.id;
		this.companyId = data.company_id;
		this.createdBy = data.created_by;
		this.userId = data.user_id;
		this.title = data.title;
		this.summary = data.summary;
		this.status = data.status;
		this.tags = data.tags;
		this.deletedAt = data.deleted_at ? parseDateSafely(data.deleted_at) : undefined;
		this.completedAt = data.completed_at ? parseDateSafely(data.completed_at) : undefined;
		this.startDate = data.start_date ? parseDateSafely(data.start_date) : undefined;
		this.endDate = data.end_date ? parseDateSafely(data.end_date) : undefined;
		// Abilities default to false if not provided
		this.abilities = {
			canView: !!data.abilities?.canView,
			canModify: !!data.abilities?.canModify,
			canDelete: !!data.abilities?.canDelete,
			canRemoveSelf: !!data.abilities?.canRemoveSelf,
		};

		// Transform activities from snake_case to camelCase, but keep data in original format
		this.activities = (data.activities || []).map((activity) => ({
			addedAt: activity.added_at,
			addedByUserId: activity.added_by_user_id,
			id: activity.id,
			type: activity.type,
			userId: activity.user_id,
			title: activity.title,
			summary: activity.summary,
			source: activity.source,
			createdAt: activity.created_at,
			updatedAt: activity.updated_at,
			data: activity.data, // Keep data in original format for Feedback/Bonus cards
		}));

		// Transform collaborators from snake_case to camelCase
		this.collaborators = (data.collaborators || []).map((collab) => ({
			id: collab.id,
			username: collab.username,
			displayName: collab.display_name,
			displayFirstName: collab.display_first_name,
			firstName: collab.first_name,
			lastName: collab.last_name,
			email: collab.email,
			fullPicUrl: collab.full_pic_url,
			profilePicUrl: collab.profile_pic_url,
			createdAt: collab.created_at,
			updatedAt: collab.updated_at,
			preferredFirstName: collab.preferred_first_name,
		}));

		this.initiative_id = data.initiative_id;
		this.initiative = data.initiative;
		this.createdAt = parseDateSafely(data.created_at);
		this.updatedAt = parseDateSafely(data.updated_at);
	}

	static fromApiData(apiData: GoalApiData): Goal {
		return new Goal(apiData);
	}

	// Transform camelCase data to snake_case for API requests
	static toApiData(data: {
		userId?: string;
		title?: string;
		summary?: string;
		status?: string;
		startDate?: string;
		endDate?: string;
		tags?: string[];
		collaborators?: string[];
		initiativeId?: string;
		completedAt?: Date;
	}): any {
		const { userId, startDate, endDate, initiativeId, completedAt, ...rest } = data;

		const transformed: any = { ...rest };

		// Transform camelCase fields to snake_case
		if (userId !== undefined) transformed.user_id = userId;
		if (startDate !== undefined) transformed.start_date = startDate;
		if (endDate !== undefined) transformed.end_date = endDate;
		if (initiativeId !== undefined && initiativeId !== '') transformed.initiative_id = initiativeId;
		if (completedAt !== undefined) transformed.completed_at = completedAt;

		return transformed;
	}
}
